import { ButcherHeader } from '@components/ButcherHeader';
import { ContactSupportModalModal } from '@components/Modal/ContactSupportModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AppSettingsFAQSteps,
  OrderProcessingMasterData,
} from '@helpers/Constants';
import { MailIcon } from '@helpers/Constants/Icons';
import { useState } from 'react';

export const FQA: React.FC = () => {
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [showContactForm, setShowContactForm] = useState<boolean>(false);

  const BreadCrumbsProps = {
    steps: AppSettingsFAQSteps,
    activeItem: 'Help/FAQ',
    type: OrderProcessingMasterData.APP_SETTINGS,
  };

  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const FaqArray = [
    {
      title: 'What is Wild Game Butcher?',
      answer:
        'Wild Game Butcher is an e-commerce platform designed for hunters to access professional butcher shop services and products. The www.wildgamebutcher.com website is comprised of various web pages operated by Techaffinity Consulting, Inc ("Taffinc")',
    },
    {
      title: 'Do I need to agree to any terms to use the site?',
      answer: 'Yes',
    },
  ];

  return (
    <div className="flex min-h-screen gap-1">
      {/* Sidebar */}
      <SideNav active={10} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          {showContactForm && (
            <ContactSupportModalModal
              onClose={() => setShowContactForm(false)}
            />
          )}
          {/* Title section */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="#1C1B1F"
                  />
                </svg>
              </button>
              <h2 className="text-xl font-semibold text-[#1C1B1F]">Help/FAQ</h2>
            </div>
          </div>

          {/* Main Content */}
          <div className="mx-auto">
            {FaqArray.map((data, index) => {
              return (
                <div key={index}>
                  <div
                    className={`border rounded-md ${openAccordions.includes(index) ? 'border-l-4 border-l-[#004834] bg-[#25831B1A]' : ''} shadow-sm mb-3 mt-4`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleAccordion(index)}
                      className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
                    >
                      <span className="text-[#474747]">{data.title}</span>
                      <div className="flex items-center gap-4">
                        <FontAwesomeIcon
                          className="text-[#000A16]"
                          icon={
                            openAccordions.includes(index)
                              ? faChevronUp
                              : faChevronDown
                          }
                        />
                      </div>
                    </button>
                    {openAccordions.includes(index) && (
                      <div className="px-4 py-2">
                        <span className="text-[#474747]">{data.answer}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-auto w-full flex items-center justify-between px-6 py-4 bg-white border border-gray-200 shadow-md rounded-xl">
            <div className="flex items-center gap-4">
              {/* Help icon */}
              <div className="flex-shrink-0">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.76 30.74C7.81333 29.9 6.12 28.76 4.68 27.32C3.24 25.88 2.1 24.1867 1.26 22.24C0.42 20.2933 0 18.2133 0 16C0 13.7867 0.42 11.7067 1.26 9.76C2.1 7.81333 3.24 6.12 4.68 4.68C6.12 3.24 7.81333 2.1 9.76 1.26C11.7067 0.42 13.7867 0 16 0C18.2133 0 20.2933 0.42 22.24 1.26C24.1867 2.1 25.88 3.24 27.32 4.68C28.76 6.12 29.9 7.81333 30.74 9.76C31.58 11.7067 32 13.7867 32 16C32 18.2133 31.58 20.2933 30.74 22.24C29.9 24.1867 28.76 25.88 27.32 27.32C25.88 28.76 24.1867 29.9 22.24 30.74C20.2933 31.58 18.2133 32 16 32C13.7867 32 11.7067 31.58 9.76 30.74ZM11.36 27.92L13.28 23.52C12.16 23.12 11.1933 22.5 10.38 21.66C9.56667 20.82 8.93333 19.84 8.48 18.72L4.08 20.56C4.69333 22.2667 5.64 23.76 6.92 25.04C8.2 26.32 9.68 27.28 11.36 27.92ZM8.48 13.28C8.93333 12.16 9.56667 11.18 10.38 10.34C11.1933 9.5 12.16 8.88 13.28 8.48L11.44 4.08C9.73333 4.72 8.24 5.68 6.96 6.96C5.68 8.24 4.72 9.73333 4.08 11.44L8.48 13.28ZM19.4 19.4C20.3333 18.4667 20.8 17.3333 20.8 16C20.8 14.6667 20.3333 13.5333 19.4 12.6C18.4667 11.6667 17.3333 11.2 16 11.2C14.6667 11.2 13.5333 11.6667 12.6 12.6C11.6667 13.5333 11.2 14.6667 11.2 16C11.2 17.3333 11.6667 18.4667 12.6 19.4C13.5333 20.3333 14.6667 20.8 16 20.8C17.3333 20.8 18.4667 20.3333 19.4 19.4ZM20.64 27.92C22.32 27.28 23.7933 26.3267 25.06 25.06C26.3267 23.7933 27.28 22.32 27.92 20.64L23.52 18.72C23.12 19.84 22.5067 20.8067 21.68 21.62C20.8533 22.4333 19.8933 23.0667 18.8 23.52L20.64 27.92ZM23.52 13.2L27.92 11.36C27.28 9.68 26.3267 8.20667 25.06 6.94C23.7933 5.67333 22.32 4.72 20.64 4.08L18.8 8.56C19.8933 8.96 20.84 9.56667 21.64 10.38C22.44 11.1933 23.0667 12.1333 23.52 13.2Z"
                    fill="#004834"
                  />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-[#1A1A1A]">
                  Still Have Any Question?
                </span>
                <span className="text-sm text-[#6C757D]">
                  Our support team is here to assist you with any questions or
                  concerns
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowContactForm(true)}
              className="flex-shrink-0 flex items-center gap-2 bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-md"
            >
              {/* Mail icon */}
              <MailIcon fill="white" />
              <span className="font-medium">Contact Support</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
